import React from "react";

// Badge 컴포넌트
// - bg: 배경색 (Tailwind 클래스(begins with "bg-") 또는 CSS 색상 문자열 가능)
// - color: 글자색 (Tailwind 클래스(begins with "text-") 또는 CSS 색상 문자열 가능)
// - size: 'sm' | 'md' | 'lg' | 'xl' (사이즈에 따른 padding / font-size 제어)
// - children: 표시할 내용 (문자열 또는 React 노드)

export interface BadgeProps {
  bg?: string;
  color?: string;
  size?: "sm" | "md" | "lg" | "xl";
  children?: React.ReactNode;
  className?: string;
  outline?: boolean;
}

const sizeClassMap: Record<NonNullable<BadgeProps["size"]>, string> = {
  sm: "px-8 py-1.6 text-[1.4rem]",
  md: "px-7 py-3 text-[1.6rem]",
  lg: "px-8 py-5 text-[1.8rem]",
  xl: "px-8 py-6 text-[2.0rem]",
};

export const Badge: React.FC<BadgeProps> = ({
  bg,
  color,
  size = "md",
  children,
  className = "",
  outline = false,
}) => {
  const classes = ["inline-flex items-center rounded-lg font-medium", sizeClassMap[size]];

  const style: React.CSSProperties = {};

  if (outline) {
    // outline 스타일: 투명 배경 + 테두리
    classes.push("bg-transparent", "border");

    // 테두리 색상 처리: Tailwind 클래스이면 border-로 변환, 아니면 inline border
    if (bg) {
      if (bg.startsWith("bg-")) {
        classes.push(bg.replace(/^bg-/, "border-"));
      } else {
        style.border = `1px solid ${bg}`;
      }
    } else {
      classes.push("border-gray-200");
    }

    // 텍스트 색상: 우선 color prop, 없으면 bg에서 유도
    if (color) {
      if (color.startsWith("text-")) {
        classes.push(color);
      } else {
        style.color = color;
      }
    } else if (bg && bg.startsWith("bg-")) {
      classes.push(bg.replace(/^bg-/, "text-"));
    }
  } else {
    // 기본(채움) 스타일
    // bg가 Tailwind 클래스 형식이면 class로 적용, 아니면 inline style로 적용
    if (bg) {
      if (bg.startsWith("bg-")) {
        classes.push(bg);
      } else {
        style.backgroundColor = bg;
      }
    }

    // color가 Tailwind 클래스 형식이면 class로 적용, 아니면 inline style로 적용
    if (color) {
      if (color.startsWith("text-")) {
        classes.push(color);
      } else {
        style.color = color;
      }
    }
  }

  if (className) classes.push(className);

  return (
    <span className={classes.join(" ")} style={style}>
      {children}
    </span>
  );
};

export default Badge;
